import type { ApiErrorBody } from './types';

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly requestId?: string;
  readonly details?: Record<string, unknown>;

  constructor(message: string, status: number, body?: ApiErrorBody) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = body?.error?.code;
    this.requestId = body?.error?.requestId;
    this.details = body?.error?.details;
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
let csrfToken: string | null = null;
// express-session regenerates the session on these routes, which invalidates the cached CSRF token.
// Signing in now happens mid-onboarding, so a stale token would break the publish that follows it.
const sessionResetRoutes = /^\/api\/auth\/(login|register|logout)$/;

async function getCsrfToken(force = false): Promise<string | null> {
  if (csrfToken && !force) return csrfToken;
  const response = await fetch('/api/auth/csrf', { credentials: 'include' });
  if (!response.ok) return null;
  const body = (await response.json()) as { csrfToken?: string; result?: { csrfToken?: string } };
  csrfToken = body.csrfToken ?? body.result?.csrfToken ?? null;
  return csrfToken;
}

async function request<T>(path: string, init: { method?: Method; body?: unknown; signal?: AbortSignal } = {}, retried = false): Promise<T> {
  const method = init.method ?? 'GET';
  const mutating = !['GET'].includes(method);
  const token = mutating ? await getCsrfToken(retried) : null;
  const response = await fetch(path, {
    method,
    credentials: 'include',
    signal: init.signal,
    headers: {
      ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { 'X-CSRF-Token': token } : {}),
    },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });

  const contentType = response.headers.get('content-type') ?? '';
  const body = contentType.includes('json')
    ? ((await response.json()) as unknown)
    : await response.text();

  if (!response.ok) {
    const parsed = typeof body === 'object' && body !== null ? (body as ApiErrorBody) : undefined;
    if (mutating && !retried && response.status === 403 && parsed?.error?.code === 'CSRF_INVALID') {
      csrfToken = null;
      return request<T>(path, init, true);
    }
    const plainMessage = typeof body === 'string' && body ? body : undefined;
    const message = parsed?.error?.message ?? parsed?.message ?? plainMessage ?? `Request failed (${response.status})`;
    throw new ApiRequestError(message, response.status, parsed);
  }

  if (mutating && sessionResetRoutes.test(path.split('?')[0])) csrfToken = null;
  if (typeof body === 'object' && body !== null && 'csrfToken' in body && typeof body.csrfToken === 'string') {
    csrfToken = body.csrfToken;
  }

  if (typeof body === 'object' && body !== null && 'result' in body) {
    return (body as { result: T }).result;
  }
  return body as T;
}

export const api = {
  get<T>(path: string, signal?: AbortSignal) {
    return request<T>(path, { signal });
  },
  post<T>(path: string, body?: unknown) {
    return request<T>(path, { method: 'POST', body });
  },
  patch<T>(path: string, body?: unknown) {
    return request<T>(path, { method: 'PATCH', body });
  },
  delete<T>(path: string, body?: unknown) {
    return request<T>(path, { method: 'DELETE', body });
  },
};

export function params(input: Record<string, string | number | boolean | undefined | null>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(input)) {
    if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
  }
  const text = query.toString();
  return text ? `?${text}` : '';
}
